import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { trackMixpanelEvent } from 'utils/analytics';

import OperationCelebration from '@/modules/Onboarding/components/OperationCelebration';
import { useCreateTechnicalOperation } from '@/modules/workingDay/apiHooks';
import { useCreateTechnicalOperationUtils } from '@/modules/workingDay/components/CreateTechnicalOperation/logic';
import { IWorkingDay } from '@/modules/workingDay/types';
import ConfirmationModal from '@/shared/components/molecules/ConfirmationModal';
import MultiStepForm from '@/shared/components/molecules/MultiStepForm';
import { useAuth } from '@/shared/hooks/context/auth';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

const CreateTechnicalOperation = () => {
  const { t } = useTranslation();
  const { openModal } = useModal();
  const { user } = useAuth();
  const [selectedAsset] = useStore((store) => store?.asset);

  const { operationFormValidationSchema, getFormSteps, getFormValues } =
    useCreateTechnicalOperationUtils();
  const { mutate, isLoading } = useCreateTechnicalOperation();

  const methods = useForm<IWorkingDay.IOperationFormValuesSchema>({
    resolver: yupResolver(operationFormValidationSchema),
    defaultValues: {
      //  TODO: dynamic value for recurring param once implemented
      recurring: false,
      entityId: selectedAsset?.id,
    },
    mode: 'all',
  });

  const onSubmit: SubmitHandler<IWorkingDay.IOperationFormValuesSchema> = (
    values
  ) => {
    const newValues = getFormValues(values);
    mutate(newValues, {
      onSuccess: (data) => {
        data.firstOperation
          ? openModal(<OperationCelebration />, {
              onClose: (closeModal) => closeModal(3),
            })
          : openModal(
              <ConfirmationModal
                header={t('create-operation-success')}
                variant='success'
                onDismissRemoveNumber={3}
              />,
              {
                dismissable: false,
              }
            );
        trackMixpanelEvent('Technical Operation Created', user);
      },
    });
  };

  return (
    <MultiStepForm<IWorkingDay.IOperationFormValuesSchema>
      steps={getFormSteps()}
      formMethods={methods}
      onSubmit={onSubmit}
      isMutating={isLoading}
      submitButtonLabel={t('create-operation')}
    />
  );
};

export { CreateTechnicalOperation };
